import { createFileRoute } from '@tanstack/react-router'
import { RefreshCw, Share2, Users } from 'lucide-react'
import { useReferralStats } from '@/lib/referrals'

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
    </div>
  )
}

function ReferralsPage() {
  const { data, isLoading, isError, error, isFetching, refetch } = useReferralStats()

  return (
    <div className="p-2 md:p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-6 h-6" /> Referral Program
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Every customer gets a shareable code — a new customer who redeems one earns a{' '}
            {'10%'} discount on their first order, applied for real by OrderService at checkout.
          </p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">Failed to load referral stats: {(error as Error)?.message}.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Codes generated" value={data?.totalCodes ?? 0} />
            <StatCard label="Codes redeemed" value={data?.totalRedemptions ?? 0} />
            <StatCard label="Converted to a paid order" value={data?.totalConverted ?? 0} />
          </div>

          <div>
            <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-white">
              <Users className="w-5 h-5" /> Top referrers
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {!data || data.topReferrers.length === 0 ? (
                <p className="p-6 text-gray-500">No referrals redeemed yet.</p>
              ) : (
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                    <tr>
                      <th className="px-4 py-2">Referrer</th>
                      <th className="px-4 py-2">Code</th>
                      <th className="px-4 py-2">Redeemed</th>
                      <th className="px-4 py-2">Converted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topReferrers.map((r) => (
                      <tr key={r.ownerUserId} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.ownerUserId}</td>
                        <td className="px-4 py-3 font-mono text-gray-800 dark:text-gray-100">{r.code}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.redeemedCount}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.convertedCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/referrals/')({
  component: ReferralsPage,
})
